import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { ordersService } from "../../../services/orders";

const SEVERITIES = ["minor", "major", "total_loss"];
const STATUSES = ["open", "investigating", "charged", "resolved"];

export function DamageFormModal({ open, onOpenChange, initial, onSubmit, submitting, lockedOrderId, lockedOrderNumber }) {
  const { t } = useTranslation();
  const isEdit = Boolean(initial?.id);
  // A13: when opened from an order detail page, the order is fixed.
  const isLocked = Boolean(lockedOrderId) && !isEdit;

  const [orderId, setOrderId] = useState(initial?.order_id || lockedOrderId || "");
  const [productId, setProductId] = useState(initial?.product_id || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [severity, setSeverity] = useState(initial?.severity || "minor");
  const [status, setStatus] = useState(initial?.status || "open");
  const [chargeAmount, setChargeAmount] = useState(
    initial?.charge_amount_eur != null ? String(initial.charge_amount_eur) : ""
  );
  const [notes, setNotes] = useState(initial?.notes || "");
  const [errors, setErrors] = useState({});

  // Orders list (loaded on open for the create flow only)
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  // Product options — items of the currently-selected order
  const [productOptions, setProductOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setOrderId(initial?.order_id || lockedOrderId || "");
    setProductId(initial?.product_id || "");
    setDescription(initial?.description || "");
    setSeverity(initial?.severity || "minor");
    setStatus(initial?.status || "open");
    setChargeAmount(
      initial?.charge_amount_eur != null ? String(initial.charge_amount_eur) : ""
    );
    setNotes(initial?.notes || "");
    setErrors({});
    // Load orders once per open (for create); edit keeps order locked.
    // A13: when order is pre-locked (from OrderDetailPage), skip loading.
    if (!isEdit && !isLocked) {
      setOrdersLoading(true);
      ordersService
        .listAll({
          include_drafts: false,
          sort: "created_at:desc",
          page: 1,
          page_size: 100,
        })
        .then((d) => setOrders(d.items || []))
        .catch(() => setOrders([]))
        .finally(() => setOrdersLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  // Populate product options when an order is chosen
  const loadProductsForOrder = useCallback(
    async (id) => {
      if (!id) {
        setProductOptions([]);
        return;
      }
      try {
        const order = await ordersService.get(id);
        setProductOptions(
          (order.items || []).map((it) => ({
            id: it.product_id,
            label: it.product_name_snapshot,
          }))
        );
      } catch {
        setProductOptions([]);
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      // For edit, the product is fixed — surface a single option label.
      setProductOptions(
        initial?.product_id
          ? [{ id: initial.product_id, label: initial.product_name || "—" }]
          : []
      );
    } else if (isLocked) {
      // A13: locked order — always resolve products for it.
      loadProductsForOrder(lockedOrderId);
      setProductId("");
    } else {
      loadProductsForOrder(orderId);
      setProductId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, open]);

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!isEdit) {
      const effectiveOrderId = isLocked ? lockedOrderId : orderId;
      if (!effectiveOrderId) errs.order_id = t("admin.damages.err_required");
      if (!productId) errs.product_id = t("admin.damages.err_required");
    }
    if (description.trim().length < 10)
      errs.description = t("admin.damages.err_description");
    if (chargeAmount !== "") {
      const n = Number(chargeAmount);
      if (Number.isNaN(n) || n < 0) errs.charge_amount_eur = t("admin.damages.err_charge");
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const payload = {
      description: description.trim(),
      severity,
      status,
      charge_amount_eur: chargeAmount === "" ? null : Number(chargeAmount),
      notes: notes.trim() || null,
    };
    if (!isEdit) {
      payload.order_id = isLocked ? lockedOrderId : orderId;
      payload.product_id = productId;
    }
    onSubmit(payload);
  };

  const orderOptions = useMemo(
    () =>
      orders.map((o) => ({
        id: o.id,
        label: `${o.order_number ? "#" + o.order_number : "—"} · ${o.event_name || "?"}`,
      })),
    [orders]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="damage-form-modal"
        className="w-full max-w-2xl mx-4 sm:mx-auto border-border-subtle bg-surface text-text-primary p-6 sm:p-8 [&_button.absolute]:text-text-secondary max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle data-testid="damage-form-title" className="text-xl">
            {isEdit
              ? t("admin.damages.edit_title", { number: initial?.damage_number || "" })
              : t("admin.damages.add_title")}
          </DialogTitle>
          <DialogDescription className="text-text-secondary">
            {t("admin.damages.form_description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>
                {t("admin.damages.field_order")}
                {!isEdit && !isLocked && <span className="ml-0.5 text-status-unavailable">*</span>}
              </Label>
              {isEdit || isLocked ? (
                <div
                  className="rounded-md border border-border-subtle bg-offwhite px-3 py-2 font-mono text-xs text-text-secondary"
                  data-testid="damage-order-readonly"
                >
                  {isLocked
                    ? lockedOrderNumber
                      ? `#${lockedOrderNumber}`
                      : lockedOrderId?.slice(0, 8) || "—"
                    : initial?.order?.order_number
                    ? `#${initial.order.order_number}`
                    : initial?.order_number
                    ? `#${initial.order_number}`
                    : initial?.order_id?.slice(0, 8) || "—"}
                </div>
              ) : (
                <Select value={orderId} onValueChange={setOrderId}>
                  <SelectTrigger
                    data-testid="damage-order"
                    className="h-10 bg-surface border-border-subtle"
                  >
                    <SelectValue
                      placeholder={
                        ordersLoading
                          ? t("common.loading")
                          : t("admin.damages.order_placeholder")
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {orderOptions.map((o) => (
                      <SelectItem
                        key={o.id}
                        value={o.id}
                        data-testid={`damage-order-option-${o.id}`}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {errors.order_id && (
                <p className="text-xs text-status-unavailable">{errors.order_id}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>
                {t("admin.damages.field_product")}
                {!isEdit && <span className="ml-0.5 text-status-unavailable">*</span>}
              </Label>
              <Select
                value={productId}
                onValueChange={setProductId}
                disabled={isEdit || !orderId}
              >
                <SelectTrigger
                  data-testid="damage-product"
                  className="h-10 bg-surface border-border-subtle disabled:opacity-60"
                >
                  <SelectValue
                    placeholder={
                      !orderId && !isEdit
                        ? t("admin.damages.product_disabled")
                        : t("admin.damages.product_placeholder")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map((p) => (
                    <SelectItem
                      key={p.id}
                      value={p.id}
                      data-testid={`damage-product-option-${p.id}`}
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.product_id && (
                <p className="text-xs text-status-unavailable">{errors.product_id}</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>
              {t("admin.damages.field_description")}
              <span className="ml-0.5 text-status-unavailable">*</span>
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="damage-description"
              rows={3}
              maxLength={1000}
              placeholder={t("admin.damages.description_placeholder")}
              className="bg-surface border-border-subtle focus-visible:ring-champagne"
            />
            {errors.description && (
              <p className="text-xs text-status-unavailable">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>
                {t("admin.damages.field_severity")}
                <span className="ml-0.5 text-status-unavailable">*</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {SEVERITIES.map((s) => (
                  <label
                    key={s}
                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                      severity === s
                        ? "border-champagne bg-champagne/10 text-charcoal"
                        : "border-border-subtle bg-surface text-text-primary hover:bg-offwhite"
                    }`}
                    data-testid={`damage-severity-${s}`}
                  >
                    <input
                      type="radio"
                      name="severity"
                      value={s}
                      checked={severity === s}
                      onChange={() => setSeverity(s)}
                      className="sr-only"
                    />
                    {t(`admin.damages.severity_${s}`)}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>
                {t("admin.damages.field_status")}
                <span className="ml-0.5 text-status-unavailable">*</span>
              </Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  data-testid="damage-status"
                  className="h-10 bg-surface border-border-subtle"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`damage-status-option-${s}`}
                    >
                      {t(`admin.damages.status_${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("admin.damages.field_charge_amount")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={chargeAmount}
                onChange={(e) => setChargeAmount(e.target.value)}
                data-testid="damage-charge-amount"
                placeholder="0.00"
                className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
              />
              {errors.charge_amount_eur && (
                <p className="text-xs text-status-unavailable">
                  {errors.charge_amount_eur}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("admin.damages.field_notes")}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="damage-notes"
              rows={2}
              maxLength={1000}
              className="bg-surface border-border-subtle focus-visible:ring-champagne"
            />
          </div>

          <DialogFooter className="pt-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="damage-cancel"
              className="border-border-subtle text-text-primary hover:bg-offwhite"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="damage-submit"
              className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.saving")}
                </>
              ) : (
                t("common.save")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
